import mapValues from 'lodash/mapValues';
import {
  validateColumnHeaders,
  validatePeriods,
} from './xlsxFile.utils';
import {
  getSheet,
  mapToJson,
  normalizeSheet,
  normalizeCellToString,
  normalizeCellToNumber,
} from '../utils/readXlsxFile.utils';
import readXlsxFile from '../utils/readXlsxFile';
import columnIndexToName from '@common/xlsxExport/utils/columnIndexToName';

const PERIOD_COLUMN_INDEX = 0;
const COMPANY_CODE_COLUMN_INDEX = 1;

const toStringParser = value => value.toString();

const fieldMappings = {
  TrialBalance: {
    period: 'period_name',
    companycode: 'company_code',
    account: 'account',
    currency: 'currency',
    ytdbalance: 'ytd_balance',
    tradingpartner: 'trading_partner',
    costcenter: 'cost_center',
    geography: 'geography',
    projectcode: 'project_code',
    reference: 'reference',
    booktype: 'book_type',
    productline: 'product_line',
  },
  BeginningBalance: {
    period: 'period_name',
    companycode: 'company_code',
    account: 'account',
    currency: 'currency',
    begbalance: 'beg_balance',
    tradingpartner: 'trading_partner',
    costcenter: 'cost_center',
    geography: 'geography',
    projectcode: 'project_code',
    reference: 'reference',
    booktype: 'book_type',
    productline: 'product_line',
  },
};

export const getFieldMappings = isBeginningBalance => {
  const mappingsForPage = isBeginningBalance ? fieldMappings.BeginningBalance : fieldMappings.TrialBalance;
  return mappingsForPage;
};

const TrialBalanceFieldParsers = {
  localaccount: toStringParser,
  ytdbalance: toStringParser,
  begbalance: toStringParser,
  booktype: toStringParser,
  geography: toStringParser,
  projectcode: toStringParser,
  reference: toStringParser,
  productline: toStringParser,
  costcenter: toStringParser,
  function: toStringParser,
};

const TrialBalanceNormalizers = {
  companycode: normalizeCellToString,
  begbalance: normalizeCellToNumber,
  tradingpartner: normalizeCellToString,
  account: normalizeCellToString,
};


const NormalizedSheetNames = {
  TRIAL_BALANCE: 'trialbalance',
  ACCOUNT_INFORMATION: 'accountinformation',
  BATCH_TAX_ENTRIES: 'TPC Subledger Import',
};

const ValidationErrors = {
  ColumnHeaders: () => new Error('An error occurred importing the file. Please examine column headers.'),
  ColumnError: column => new Error(`An error occurred importing the file. Please examine column ${columnIndexToName(column)}.`),
  InvalidPeriod: column => new Error(`An error occurred importing the file. Invalid period name in column ${columnIndexToName(column)}`),
  InvalidCompanyCode: column => new Error(`An error occurred importing the file. Invalid company code in column ${columnIndexToName(column)}`),
  MissingCompanyCode: column => new Error(`An error occurred importing the file. Missing company code in column ${columnIndexToName(column)}`),
  MissingPeriod: column => new Error(`An error occurred importing the file. Missing period in column ${columnIndexToName(column)}`),
};

const trialBalanceReadXlsxFileAsJson = async ({
  file,
  normalizedSheetName,
  fieldMappings,
  fieldParsers = null,
  fieldNormalizers = null,
  validationData = null,
}) => {
  const workbook = await readXlsxFile(file);

  const mappedFieldNames = Object.keys(fieldMappings);

  const columnHeadersValidationResult = validateColumnHeaders(workbook, mappedFieldNames);
  if (columnHeadersValidationResult) {
    throw columnHeadersValidationResult > 0
      ? ValidationErrors.ColumnError(columnHeadersValidationResult)
      : ValidationErrors.ColumnHeaders();
  }

  if (validationData) {
    if (!validatePeriods(workbook, validationData)) {
      throw ValidationErrors.InvalidPeriod(PERIOD_COLUMN_INDEX);
    }
  }

  const sheetToMapRaw = getSheet(workbook, normalizedSheetName);

  const sheetToMap = fieldNormalizers ? normalizeSheet(fieldNormalizers)(sheetToMapRaw) : sheetToMapRaw;

  const mappedSheet = mapToJson({
    sheet: sheetToMap,
    fieldMappings,
    fieldParsers,
    options: {
      raw: true,
      range: sheetToMap['!ref'],
    },
  });

  const trimmedSheet = mappedSheet.map(row => mapValues(row,
    value => (value && value.trim) ? value.trim() : value
  ));

  if (!validationData) {
    return trimmedSheet;
  }

  const companyCodesMissing = !trimmedSheet.every(
    row => row.company_code || (row.management_entity && row.legal_entity)
  );
  if (companyCodesMissing) {
    throw ValidationErrors.MissingCompanyCode(COMPANY_CODE_COLUMN_INDEX);
  }

  const periodsMissing = !trimmedSheet.every(row => row.period_name);
  if (periodsMissing) {
    throw ValidationErrors.MissingPeriod(PERIOD_COLUMN_INDEX);
  }

  return trimmedSheet;
};

const readTrialBalanceXlsxFileAsJson = (file, validationData, isBeginningBalance) => trialBalanceReadXlsxFileAsJson({
  file,
  normalizedSheetName: NormalizedSheetNames.TRIAL_BALANCE,
  fieldMappings: getFieldMappings(isBeginningBalance),
  fieldParsers: TrialBalanceFieldParsers,
  fieldNormalizers: TrialBalanceNormalizers,
  validationData,
});


export {
  readTrialBalanceXlsxFileAsJson,
};
